import { capitalize } from 'lodash-es';
import { SYSTEM_FIELD_MAX_FIELD_LENGTH } from "@/services/constants";
import { MergeFieldsMaxFieldsValuesMap } from "@/store/mergeFields/types";
import { ContactFieldType, MergeField } from "@dev/ee-api/ee-api-models";
import { EditorSignUpField, EditorSignUpFieldType } from "@dev/template-editor";
import i18n from '@/../i18n';

interface SignUpFieldType {
    type: EditorSignUpFieldType;
    attributes?: Record<string, string>;
}

const getTemplateEditorSignUpFieldDetails = (
    field: MergeField,
    maxFieldsValuesMap: MergeFieldsMaxFieldsValuesMap
): SignUpFieldType => {
    const { FieldType: type, MaxSize: maxLength, CollectValues: values } = field ?? {};

    switch (type) {
        case ContactFieldType.Number:
        case ContactFieldType.Currency:
        case ContactFieldType.BigNumber: {
            return {
                type: EditorSignUpFieldType.NUMBER,
                attributes: {
                    min: maxFieldsValuesMap?.[type]?.Min,
                    max: maxFieldsValuesMap?.[type]?.Max,
                },
            };
        }
        case ContactFieldType.Boolean: {
            return {
                type: EditorSignUpFieldType.CHECKBOX,
                attributes: { value: 'true' },
            };
        }
        case ContactFieldType.DateTime: {
            return {
                type: EditorSignUpFieldType.DATETIME,
                attributes: {
                    placeholder: 'dd.mm.yyyy HH:mm',
                    pattern: '([0-9]{2}.[0-9]{2}.[0-9]{4} [0-9]{2}:[0-9]{2})',
                },
            };
        }
        case ContactFieldType.Date: {
            return {
                type: EditorSignUpFieldType.DATE,
                attributes: {
                    placeholder: 'dd.mm.yyyy',
                    pattern: '([0-9]{2}.[0-9]{2}.[0-9]{4})',
                },
            };
        }
        case ContactFieldType.Email: {
            return {
                type: EditorSignUpFieldType.EMAIL,
                attributes: {
                    maxlength: `${SYSTEM_FIELD_MAX_FIELD_LENGTH}`,
                },
            };
        }
        default: {
            if (type === ContactFieldType.Text && values?.length === 1) {
                return {
                    type: EditorSignUpFieldType.CHECKBOX,
                    attributes: { value: values[0] },
                };
            }
            return {
                type: EditorSignUpFieldType.TEXT,
                attributes: {
                    maxlength: `${maxLength > 0 ? maxLength : SYSTEM_FIELD_MAX_FIELD_LENGTH}`,
                },
            };
        }
    }
};

const REQUIRED_FIELDS = ['email'];
const DEFAULT_FIELDS = ['email'];

export const mapMergeFieldToEditorSignUpField = (
    field: MergeField,
    maxFieldsValuesMap: MergeFieldsMaxFieldsValuesMap
): EditorSignUpField => {
    const fieldDetails = getTemplateEditorSignUpFieldDetails(field, maxFieldsValuesMap);
    const placeholderTranslationKey = `formPlaceholder_${field.CollectName}`;
    const placeholderTranslation = i18n.t(`formPlaceholder_${field.CollectName}`);
    return {
        id: field.Name,
        name: field.CollectName,
        type: fieldDetails.type,
        isDefault: DEFAULT_FIELDS.includes(field.Name),
        isRequired: REQUIRED_FIELDS.includes(field.Name),
        isRemovingBlocked: REQUIRED_FIELDS.includes(field.Name),
        label: capitalize(field.DisplayName ?? field.Name),
        placeholder: placeholderTranslationKey === placeholderTranslation ? '' : placeholderTranslation,
        inputAttributes: fieldDetails.attributes,
    };
};
